import { useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { SelectField, Options } from '../components/Field.jsx';
import { Loader, ErrorAlert, EmptyRow } from '../components/States.jsx';
import { useGetSalesReportQuery } from '../store/apiSlice.js';
import { useReferenceData } from '../store/useReferenceData.js';
import { fmtDate, today, firstOfThisMonth, money } from '../utils/format.js';

export default function SalesReportPage() {
  const { customers } = useReferenceData();

  const [from, setFrom] = useState(firstOfThisMonth());
  const [to, setTo] = useState(today());
  const [customer, setCustomer] = useState('');

  // Don't ask the server for a range that cannot exist.
  const rangeError = from && to && from > to ? '"Date from" must be on or before "Date to"' : '';

  // RTK Query keys the cache on the argument object, so no manual key is needed.
  // `skip` keeps an impossible date range from ever reaching the server.
  const {
    data,
    isFetching: loading,
    error: apiError,
    refetch: reload,
  } = useGetSalesReportQuery({ from, to, customer }, { skip: Boolean(rangeError) });

  const error = apiError?.message ?? null;
  const rows = data?.rows ?? [];
  const meta = data?.meta ?? null;

  const clearFilter = () => {
    setFrom('');
    setTo('');
    setCustomer('');
  };

  return (
    <>
      <PageHeader
        eyebrow="Reports"
        title="Sales Report"
        subtitle="Filter by date range and customer. The grand total appears below the table."
      />

      <Card title="Date Range Filter" tag="Date From → To">
        <div className="form-grid cols-3">
          <div className="field">
            <label htmlFor="sr-from">Date From</label>
            <input
              id="sr-from" type="date" value={from} max={today()}
              className={rangeError ? 'invalid' : undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
            {rangeError && <div className="field-error">{rangeError}</div>}
          </div>
          <div className="field">
            <label htmlFor="sr-to">Date To</label>
            <input
              id="sr-to" type="date" value={to} max={today()} min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <SelectField label="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)}>
            <Options items={customers.map((c) => c.company)} placeholder="All customers" />
          </SelectField>
        </div>
        <div className="btn-row">
          <button type="button" className="btn ghost" onClick={clearFilter}>
            Clear filter
          </button>
        </div>
      </Card>

      <Card title="Sales Entries" tag="Filtered Results">
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice No.</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Units</th>
                    <th>GST (₹)</th>
                    <th>Grand Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {rangeError ? (
                    <EmptyRow colSpan={7}>Fix the date range to see results</EmptyRow>
                  ) : !rows.length ? (
                    <EmptyRow colSpan={7}>No sales in this date range</EmptyRow>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td className="nowrap">{fmtDate(row.date)}</td>
                        <td className="nowrap">{row.invoiceNo}</td>
                        <td>{row.customer}</td>
                        <td className="num">{row.items}</td>
                        <td className="num">{row.units}</td>
                        <td className="num">₹{money(row.totalGst)}</td>
                        <td className="num">₹{money(row.grandTotal)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="grand-total-line" style={{ marginTop: 14 }}>
              Total: ₹{money(meta?.grandTotal ?? 0)}
              <div className="sub-line text-right" style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 400 }}>
                {meta?.invoices ?? 0} invoice(s) · GST included ₹{money(meta?.totalGst ?? 0)}
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
